/**
 * Registry of Stedi payers for which `memberId` (policy number) is NOT
 * strictly required on the 270 subscriber segment.
 *
 * Stedi's payer directory tags each payer with a "Member ID optional"
 * confidence level:
 *   - "Any member succeeds without a member ID (>90% success rate)"
 *   - "The majority of members succeed without a member ID"
 *   - "A subset of plans and members succeed without a member ID"
 * For all three tiers we allow the check to proceed without a memberId —
 * a subset still succeeding is better than blocking entry at the form.
 *
 * Shared between frontend (form validation) and backend (verifyEligibility
 * argument check) so both sides stay in lockstep.
 *
 * Adding a payer: append its stable Stedi `stediId` to the list below — NOT
 * its `tradingPartnerServiceId`. They are different fields on the same payer
 * (for CMS the stediId is "JDNSN" while the request id is "CMS" — see
 * functions/src/eligibilityCheck/utils/medicare.ts), and only the stediId is
 * stable across directory updates. The `name` comment is for humans only —
 * lookup is by ID.
 */

const MEMBER_ID_OPTIONAL_PAYER_IDS_LIST: readonly string[] = [
  "HGJLR", // Cigna
  "KMQTZ", // UnitedHealthcare
  "DACBS", // Independence Blue Cross Pennsylvania
  "HPQRS", // Aetna
  "UHXHC", // Prominence Health Plan
  "ICMUO", // Indiana Medicaid
  "EWDCI", // Blue Cross Blue Shield Massachusetts
  "PKXKN", // University of Pittsburgh Medical Center Health Plan
  "ZXHCL", // Imagine360
  "VAOXB", // Keystone First Community HealthChoices
  "TYVLW", // Medicaid Louisiana
  "SMZIL", // Medicaid Ohio
  "ISXVB", // Fidelis Care
  "QKPQU", // Medicaid Virginia
  "UWTOI", // UMR (United Medical Resources Wausau)
  "EPAJK", // CHAMPVA HAC
  "VLBIY", // Medicaid New Hampshire
  "LAESW", // Anthem Blue Cross Blue Shield of California
  "OLQXL", // Anthem Blue Cross Blue Shield of New York
  "JSZHK", // Medicaid Minnesota
  "UPICO", // Blue Cross NC
  "PACIU", // Medicaid Illinois
  "EGASA", // WellCare
  "IEMTN", // Health First Colorado
  "FRTBO", // Medicaid Wyoming
  "UCRIE", // Health Alliance Plan
  "CLOAF", // Priority Health
  "TAZWT", // Oscar Health
  "TGXAN", // Keystone First Pennsylvania Community HealthChoices
  "XJWMR", // Magellan HealthCare
  "ZKMIW", // Molina Healthcare California
  "UYORK", // Humana
  "HZWKL", // Mutual of Omaha
  "MOPSB", // Medicaid Oklahoma
  "LSJBA", // Regence BlueShield of Washington
  "IEBAC", // Allegiance Benefit Plan Management Incorporated
  "TLJRV", // Medicaid Kentucky
  "WZMTQ", // MVP Health Plan
  "NRNBT", // Medical Mutual
  "ZKCOE", // Harvard Pilgrim Health Care
  "QEFMR", // West Virginia Medicaid
  "HNRTE", // Regence BlueCross BlueShield of Utah
  "WRUYL", // CareSource of Georgia
  "XGNDF", // Medica
  "WVPHC", // Student Resources (UnitedHealthcare)
  "KCLFN", // Medicaid Washington
  "QXSCY", // UCare Minnesota
  "IPMKW", // Molina Healthcare Florida
  "YNKWF", // Medicaid Pennsylvania
  "QHVSU", // CareSource Medicaid Ohio
  "DRWRY", // Anthem Blue Cross Blue Shield of Connecticut
  "CYRGF", // Oxford
  "QDINL", // PacificSource
  "ZRTGI", // Medicaid Oregon
  "BMYAQ", // Molina HealthCare Ohio
  "WLKCN", // Blue Cross Blue Shield of Minnesota - Commercial and Medicare
  "VSAZE", // Regence BlueCross BlueShield of Oregon
  "IGEJI", // Molina Healthcare Utah
  "NEMUT", // HealthPartners (MN)
  "JUQVZ", // Colorado Access
  "QRPMU", // Cigna HealthSpring
  "BHNXS", // Anthem Blue Cross Blue Shield Ohio
  "OIAZC", // Blue Cross Blue Shield of Nebraska
  "THTLS", // Regence BlueShield of Idaho
  "CKANP", // Scott & White Health Plan
  "KUHOB", // Capital Blue Cross of Pennsylvania
  "KWDBT", // Medicaid Massachusetts
  "TLTBQ", // Select Health
  "PYEIO", // MetroPlusHealth
  "BEUZA", // Medicaid Arizona
  "VESYH", // Blue Cross Blue Shield of Kansas
  "WQXDR", // Dean Health Plan
  "COINY", // Medicaid Delaware
  "AGBDX", // Blue Cross Blue Shield Mississippi
  "BONSV", // Medicaid Arkansas
  "DXOAW", // AARP UnitedHealthCare Insurance Company
  "XJXMN", // Educators Mutual (EMIA)
  "ZHUHM", // HealthSmart Benefit Solutions
  "HWLNZ", // Sutter Select
  "RLTDR", // Medicaid Connecticut
  "IVMAV", // PacificSource Community Solutions
  "FBMFK", // Aetna Better Health Texas
  "VLFZU", // Anthem Blue Cross Blue Shield of Wisconsin
  "IJBEO", // Western Health Advantage
  "YKJYW", // Partnership HealthPlan of California
  "JGCQW", // Meritain Health
  "ESOMB", // Community Health Choice
  "NKORC", // Dean Health Plans
  "GBAEC", // Atrio Health Plans
  "BSNZF", // Premera Blue Cross of Washington
  "JMOXY", // L.A. Care Health Plan (California)
  "JEKWK", // Asuris Northwest Health
  "DQFAF", // Molina Healthcare Michigan
  "NPFPE", // Meritain Health
  "KGYKW", // AultCare
  "BKYTB", // Allied Benefit Systems
  "NANCR", // AmeriHealth Caritas North Carolina
  "MNQIA", // Medicaid Montana
  "CRSWV", // Medicaid Tennessee
  "JSXAD", // AmeriBen Solutions Incorporated
  "GZVQX", // Medicaid Iowa
  "IMZPF", // Health New England
  "SRICT", // Highmark Western and Northeastern New York
  "LQSAG", // Molina Healthcare New Mexico
  "CRQTA", // AmeriHealth Caritas Pennsylvania
  "JDNSN", // National Centers for Medicare & Medicaid Services
  "XKLKL", // Molina HealthCare Washington
  "ABHDB", // Community Health Plan of Washington
  "WOEDV", // Cook Children's Health Plan STAR
  "ZESMZ", // Molina Healthcare Texas
  "NSJWB", // APWUHP
  "CQGQZ", // McLaren Health Plan
  "KLBLK", // Blue Cross Blue Shield of Minnesota Blue Plus Medicaid
  "ASLGA", // Kaiser Foundation Health Plan Washington
  "QKVBU", // Medicaid North Dakota
  "IKLBE", // Gateway Health Plan
  "SDFEL", // Independent Health
  "UYOCO", // Tufts Health Plan
  "NRWAE", // Johns Hopkins Health Plans
  "DCXFI", // Reflect Health
  "INRAO", // Golden Rule
  "OEZFO", // Molina Healthcare South Carolina
  "YUFJT", // Moda Health Plan
  "QSOCK", // Surest
  "FKVUF", // Auxiant
  "SJGZV", // Health Alliance Plan
  "LEXAK", // Neighborhood Health Plan of Rhode Island (Medicaid/Commercial)
  "NOGQI", // AvMed
  "ZSXLS", // MedStar Family Choice Maryland
  "GJUXI", // Kaiser Foundation Health Plan Northwest
  "ZMNKM", // Aetna Dental Medicare
  "VDLTR", // Hawaii Mainland Administrators
  "BJEWB", // Molina Healthcare Illinois
  "AJKPI", // University of Utah Health Plans
  "FTVES", // Healthfirst
  "PVBKR", // MagnaCare
  "HUZUL", // HealthChoice of Oklahoma
  "RPQMA", // Viva Health Plan
  "IDKTK", // Medica Health Plan Solutions
  "OGIPR", // Mass General Brigham Health Plan
  "WKDLP", // Blue Cross Blue Shield of Hawaii
  "PYZLJ", // Indiana University Health Plan - Medicare Advantage
  "ZUTWK", // CBA Blue
  "AVLDT", // Aetna Better Health Illinois
  "BZHYW", // Molina Healthcare Wisconsin
  "YWVAW", // Key Benefit Administrators
  "DGFOW", // PacificSource Medicare
  "LYQNE", // Benefit Management Incorporated
  "EQNUS", // Consumers' Choice Health SC
  "VALCQ", // Aetna Better Health New York
  "AFLEJ", // All Savers
  "XAUZB", // Network Health Insurance Corporation Medicare (NHIC)
  "POYUE", // Aetna Better Health Florida
  "AHILO", // Christian Brothers Services
  "NSIEN", // Horizon New Jersey Health
  "OGLRE", // Aetna Better Health Ohio
  "STIKE", // 1199 National Benefit Fund
  "AWBDZ", // Blue Cross Blue Shield of North Dakota
  "GIFBK", // CGS Health
  "WZSCW", // Boston Medical Center HealthNet Plan
  "ZTXQE", // Elderplan
  "OVRAP", // Aetna Better Health New Jersey
  "HZSQR", // BridgeSpan Health
  "TKDCO", // Santa Clara Family Health Plan
  "ODJMF", // CountyCare
  "YBYKA", // Personify Health
  "YICPW", // Mutual Health Services
  "UQNJM", // Banner Health
  "TQJKQ", // Sana Benefits
  "ZRSYL", // Devoted Health
  "PYXZF", // Group and Pension Administrators
  "ESEPO", // Curative
  "YDWFY", // UnitedHealthcare Community Plan Tennessee
  "SQHBW", // Innovation Health
  "FDWLU", // United HealthCare Community Plan of Arizona
  "OYVLO", // Medicaid Vermont
  "AMKGH", // SCAN Health Maintenance Organization California
  "GMSIX", // Aetna Better Health Virginia
  "HSVNU", // United HealthCare Ohio Medicaid Managed Care Entity (MCE)
  "ATYQS", // UnitedHealthcare Community Plan New Jersey
  "VMWOV", // Gravie
  "AKNIL", // Inland Empire Health Plan
  "ZWHJF", // Pan-American Life Insurance Group
  "FRCPB", // Stedi Test Payer
  "SFIRK", // Samaritan Health Plans
  "EZFMA", // CareSource (Family Health Management)
  "KECXF", // Health Benefit Claims Department
  "UJMEF", // Partners Behavioral Health Management
  "RFUPF", // Medicaid Michigan
  "PWTUO", // AmeriHealth Caritas North Carolina
  "HCUQB", // Aetna Better Health
  "RFOHR", // AmeriHealth Caritas Ohio Medicaid MCE
  "MIKEM", // Unite Here Health – Las Vegas
  "PFKXV", // UnitedHealthcare Community Plan - KS (KanCare)
  "IHVFZ", // Aetna Better Health Louisiana
  "PNKDM", // Hennepin Health
  "UAVZY", // CareSource Indiana
  "KNDAF", // MetLife Dental Family
  "SVGZA", // TransAmerica Life Insurance Company
  "XQKDT", // Community Care Managed Health Care Plans Oklahoma
  "JEQRO", // UnitedHealthcare Dental
  "XSVWC", // Paramount
  "IGNEA", // National Rural Letter Carrier Association (NRLC)
  "DOGPS", // Molina Healthcare - Affinity by Molina Healthcare
  "PYIUD", // Aetna Better Health Kentucky
  "RXFEO", // Health Plan of San Mateo
  "BKYXM", // Prominence Health Plan Nevada
  "YCDLI", // Molina HealthCare Idaho
  "JFLVB", // Mercy Care Regional Behavioral Health Authority
  "ADTWS", // MAMSI Health Plan
  "JZSAE", // Cascade Health Alliance
  "BQTMB", // Network Health Plan
  "PHCYF", // Rocky Mountain Health Plans
  "FKAUR", // Premera Blue Cross Alaska
  "OVOSB", // WellMed
  "CDLWM", // AmeriHealth Caritas Next North Carolina
  "OCDPF", // Culinary Health Fund
  "DVYTB", // Passport Health Plan
  "LYJAI", // United HealthCare SecureHorizons Medicare
  "PCRNS", // Quartz
  "DPKRY", // Senior Whole Health New York
  "GQZKW", // PEHP
  "VOGRS", // Delta Dental of Oregon
  "ZJTZG", // Regence Group Administrators
  "HZGTQ", // Christian Care Ministry
  "CPTEZ", // Sun Life
  "QHYCD", // Health Partners Philadelphia
  "EPBGH", // Molina Complete Care Virginia DSNP
  "JYMNM", // CareOregon
  "CPRLY", // First Choice by Select Health South Carolina
  "BVWFA", // Johns Hopkins Advantage MD
  "XQJTN", // LifeWise Health Plan of Washington
  "FSRDJ", // Mountain Health Co-op
  "WONRS", // Independent Health
  "NPCFC", // Delta Dental of Virginia
  "PDERF", // Preferred Care Partners
  "VTXBJ", // Delta Dental of Minnesota
  "RYCDO", // Boston Medical Center HealthNet Plan
  "TGCUQ", // Cigna (DHMO)
  "AYIUS", // Prairie States Enterprises Incorporated
  "OUYJL", // MVP Health Plan
  "FZJTM", // Delta Dental of Iowa
  "GMTFQ", // Mercy Care Plan (AZ Health Care Cost Containment System)
  "RWIXI", // Sanford Health Plan
  "HEFYH", // Smoky Mountain Local Management Entity Managed Care Organization
  "MZXPZ", // Healthy Indiana Plan
  "CYARS", // VillageCareMAX
  "VNMVO", // Aetna Better Health California
  "HOFJD", // Healthplex
  "PUFBY", // Blue Cross Blue Shield of Minnesota - Commercial and Medicare
  "XZUFJ", // First Choice VIP Care Plus South Carolina
  "JSIUL", // HealthPartners Pennsylvania
  "OGKGP", // CDPHP
  "RWGFP", // Delta Dental of Missouri
  "YEVAB", // Allina Health
  "XWNAC", // Trust Eligibility Network
  "RMCYE", // New Era Life Insurance Company
  "VUQZE", // Delta Dental of Iowa - Dental Wellness Plan (DWP)
  "NPUWQ", // MedBen
  "HUGWC", // Delta Dental of Kentucky
  "HVTQZ", // Texas Health Aetna
  "CTWKL", // Delta Dental Colorado
  "NXNZF", // Skygen
  "QTJKN", // Mercy Care Arizona
  "ZNJOR", // CDPHP
  "BMSRO", // Molina HealthCare Nevada
  "NEUOX", // Aetna Better Health Oklahoma
  "BTALR", // Sutter Health Aetna
  "GRDFK", // United Medical Resources - Cincinnati
  "FITZI", // First Choice Next
  "RUPCP", // Delta Dental of Kansas
  "EZVQN", // CareSource North Carolina
  "HJTBL", // Aetna Dmo
  "CTWJH", // Centene Dental and Vision Services
  "PJXWD", // Delta Dental of South Carolina
  "GIOJF", // PrimeWest Health
  "EJVIF", // CareSource Medicaid Ohio
  "IJWQW", // Civilian Health and Medical Program of the Department of Veterans Affairs Spina Bifida Children of Women Vietnam Veterans
  "QWLGM", // Boston Medical Center HealthNet Plan
  "SNPIA", // Itasca Medical Care
  "PNMDV", // Cigna Dental (HMO)
  "OSWAN", // Medical Associates Health Plan
  "LYJWQ", // The Health Plan of West Virginia
  "WHFNQ", // AmeriHealth Caritas Louisiana (LaCare)
  "YLAZX", // Aflac Medicare Supplemental
  "PHMHA", // Municipal Health Benefit Fund
  "CKBEY", // McLaren Health Advantage
  "LWIBC", // United Medical Resources (UMR Onalaska)
  "WTZYM", // Aetna Affordable Health Choices (SM) - SRC
  "MXOHN", // LifeWise Assurance Company
  "NKTIX", // Solstice Benefits
  "QFEBR", // Apipa (Arizona Physicians IPA) - Pre Determinations Only
  "ELSAC", // Health First
  "ZXZQO", // McLaren Medicaid
  "IGKJB", // Valley Health Plan
  "LFQLE", // Group Pension Administrators (Imagine360)
  "CLSVQ", // Mass General Brigham Health Plan
  "FKWHT", // Molina Healthcare of Mississippi
  "IPTIT", // Essence Healthcare
  "CXLJF", // Healthcare Management Administrators (HMA)
  "CAOHR", // Selman and Company
  "LKDGV", // United Medical Resources - Harrington
  "SACUA", // Provident Life & Accident Insurance Company of America
  "ODHXM", // Inland Empire Health Plan (Covered California)
  "MDQTM", // AmeriHealth Caritas Northeast (AmeriHealth Northeast)
  "BXUAU", // Molina Healthcare Nebraska
  "SEZHV", // HealthPlans Incorporated
  "HIBRO", // Aetna Medicaid Illinois
  "ZIJHE", // Maryland Physicians Care
  "VFJWB", // PacifiCare Dental & Vision Plan
  "XBYLG", // Farm Bureau Health Plans MAPD
  "CUBAN", // Trust Eligibility Network
  "100237", // Medicaid Oregon
  "IRGXY", // AmeriHealth Caritas Next Florida
  "RJQLO", // Medica Individual and Family
  "LPVAD", // Community Health Choice (Marketplace)
  "TJCKL", // Cook Children's Health Plan
  "SBEGF", // UnitedHealthcare Dental
  "YTGWD", // International Benefit Administration
  "TXZQC", // Medical Associates Health Plan Health Choices
  "ODEWC", // SummaCare Health Plan
  "ZAGAR", // McLaren Medicare Supplement
  "10021", // Medicaid Arizona
  "RDSUE", // AmeriHealth Caritas Health Plan New Hampshire
  "ELOGE", // Medicaid Texas - Acute
  "HRXIB", // Keystone First
  "NWAQC", // Everence Financial
  "ACFNZ", // Blue Cross Community Centennial
  "10118", // Medicaid Louisiana
];

/**
 * Stedi payer IDs for which the memberId field is treated as optional on
 * the eligibility form. All other Stedi-routed payers require a memberId.
 */
export const MEMBER_ID_OPTIONAL_PAYER_IDS: ReadonlySet<string> = new Set(
  MEMBER_ID_OPTIONAL_PAYER_IDS_LIST,
);

/**
 * True when the given payer's stable Stedi `stediId` is on the list of payers
 * that accept 270 requests without a memberId. Returns false for empty/null
 * input and for any id not in the registry.
 *
 * Takes the `stediId`, NOT the `tradingPartnerServiceId` — the registry is
 * keyed by the stable id, and the wrong one fails silently: every lookup
 * misses, so a member-id-optional payer starts demanding a member ID with no
 * error anywhere.
 *
 * Does NOT cover RPA-routed payers (VSP, EyeMed) — those have their own
 * skip path via `resolveRpaVendor()` in `shared/rpaRegistry.ts`.
 */
export function isMemberIdOptionalForPayer(
  stediId: string | null | undefined,
): boolean {
  if (!stediId) return false;
  return MEMBER_ID_OPTIONAL_PAYER_IDS.has(stediId);
}
